import { Loader } from "@mantine/core";
import { createRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import { useGetFirstAdTiles } from "../api/generated/endpoints/generation/generation";
import { FirstAdCreation } from "../features/onboarding/FirstAdCreation";
import { FirstAdPlans } from "../features/onboarding/plans";
import { VERIFIED_ONBOARDING_PLANS } from "../features/onboarding/plans-catalog";
import { useFirstAdFlow } from "../features/onboarding/use-first-ad-flow";
import { useFirstAdDelivery } from "../features/onboarding/use-first-ad-delivery";
import { ErrorAlert } from "../platform/ErrorAlert";
import { firstAdChoice, firstAdSearch, type FirstAdSearch } from "../platform/first-ad-choice";
import type { FirstAdDestination, FirstAdPlanReason } from "../platform/first-ad";
import { brandRoute } from "../platform/routes";
import { recordFirstAdStep } from "../platform/onboarding-tracking";
import { firstAdCheckoutAttribution } from "./first-ad-attribution";
import { FirstAdActor } from "./first-ad-actor";
import { FirstAdProduct } from "./first-ad-product";

export const firstAdFlowRoute = createRoute({
  getParentRoute: () => brandRoute, path: "/first-ad/$templateId", staticData: { ownFrame: true },
  validateSearch: (search) => firstAdSearch.parse(search), component: FirstAdFlow,
});
function FirstAdFlow() {
  const destination = firstAdFlowRoute.useParams();
  const search = firstAdFlowRoute.useSearch();
  return <Creation key={JSON.stringify(destination)} destination={destination} search={search} />;
}
function Creation({ destination, search }: {
  destination: FirstAdDestination;
  search: FirstAdSearch;
}) {
  const choice = firstAdChoice({ ...search, template: destination.templateId });
  const flow = useFirstAdFlow(destination);
  const navigate = useNavigate();
  const state = flow.state.data;
  const delivery = useFirstAdDelivery({ ...destination, runId: state?.run_job_id ?? null });
  const display = state ? { ...state,
    previews: state.run_job_id ? delivery.data?.previews ?? [] : state.previews,
    problem: delivery.data?.problem ?? state.problem,
  } : undefined;
  const catalog = useGetFirstAdTiles();
  const [error, setError] = useState<unknown>(null);
  const [plans, setPlans] = useState<FirstAdPlanReason | null>(null);
  const advanced = useRef("");
  const started = useRef("");
  useEffect(() => {
    if (!state) return;
    const context = { template: destination.templateId };
    if (state.pick?.product) void recordFirstAdStep("product_found", context);
    if (state.pick?.actor) void recordFirstAdStep("actor", context);
    if (state.lines.length > 0) void recordFirstAdStep("script", context);
    const previews = state.run_job_id ? delivery.data?.previews ?? [] : state.previews;
    if (previews.length === 3 && previews.every((preview) => !!preview.url)) void recordFirstAdStep("keyframes", context);
    if (delivery.data?.complete) void recordFirstAdStep("free_ad_made", context);
  }, [state, delivery.data, destination.templateId]);
  useEffect(() => {
    const attempt = String(state?.offer?.attempt ?? 1);
    if (started.current === attempt || !state || flow.pending || flow.error || state.problem || state.working
      || state.run_job_id || state.preview_mode !== "video" || !state.video_ready) return;
    started.current = attempt;
    flow.start();
  }, [state, flow]);
  // The facade alone decides whether a configured cast transition exists and is permitted.
  // No template ids or retry entitlement are inferred in the web.
  useEffect(() => {
    if (!state || state.problem || flow.error || state.stage !== "keyframes" || state.working || state.video_ready || state.run_job_id
      || state.previews.length === 0 || state.previews.some((preview) => !preview.url)) return;
    const stamp = JSON.stringify([destination, state.previews.map(({ name, status }) => ({ name, status }))]);
    if (advanced.current === stamp) return;
    advanced.current = stamp;
    flow.advance();
  }, [state, destination, flow]);
  if (flow.state.isPending) return <Loader aria-label="Loading your first ad" />;
  if (flow.state.error) return <ErrorAlert error={flow.state.error} />;
  if (!state || !display) return null;
  const pick = state.pick;
  if (plans) return <FirstAdPlans {...destination} reason={plans} onClose={() => setPlans(null)}
    plans={VERIFIED_ONBOARDING_PLANS} checkoutEnabled={false} checkoutAttribution={firstAdCheckoutAttribution}
    onContinueToApp={() => { void navigate({ to: "/" }); }}
    onPaidContinue={() => { void navigate({ to: "/" }); }} />;
  return <FirstAdCreation state={display} phase={flow.phase} styleName={catalog.data?.items.find((tile) => tile.id === destination.templateId)?.title ?? "Your first ad"}
      videoUrl={delivery.data?.complete ? delivery.data.video_url ?? null : null} posterUrl={delivery.data?.poster_url ?? null}
      productStep={<FirstAdProduct destination={destination} link={choice?.product ?? ""} pending={flow.pending}
        onPick={(product) => flow.save({ product, actor: null, voice_id: null })} />}
      actorStep={pick ? <FirstAdActor destination={destination} pick={pick} needsVoice={state.needs_voice ?? false} pending={flow.pending}
        onPick={(actor, voice_id) => flow.save({ ...pick, actor, voice_id })}
        onContinue={() => { void flow.ask(pick).catch(setError); }} /> : null}
      tools={[{ id: "studio", label: "Studio" }, { id: "templates", label: "Templates" }, { id: "library", label: "Library" }]}
      toolsUnlocked={false} pending={flow.pending || delivery.downloading} error={error ?? flow.error ?? delivery.error ?? delivery.downloadError}
      onContinueToApp={() => { void navigate({ to: "/" }); }}
      onRetry={flow.retry}
      onApprove={flow.approve} onGetVideo={() => {
        void recordFirstAdStep("get_video", { template: destination.templateId });
        if (state.run_job_id) flow.acknowledgePreview(state.run_job_id); else flow.start();
      }} onDownload={() => void delivery.download()}
      onRefreshMedia={() => { void flow.refresh(); if (state.run_job_id) void delivery.refetch(); }} onPlans={(reason) => {
        void recordFirstAdStep("plans", { template: destination.templateId }); setPlans(reason);
      }} onOpenTool={() => {}} />;
}
